/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import type { UserLocation } from './geolocation.types';
import {
    subscribeUserLocation,
    getUserLocationSnapshot,
    getUserLocationServerSnapshot,
} from './user-location-store';

/**
 * Returns the user's approximate location, resolved once per browser session by
 * a shared module-level store and shared across every consumer.
 *
 * Resolution progresses from a sessionStorage cache to an IP lookup to browser
 * geolocation; a more accurate navigator result may replace an earlier value.
 * Because many components can call this hook, resolution is deduplicated: the
 * IP request and navigator attempt each happen at most once per session,
 * regardless of how many components are mounted.
 *
 * The hook returns `UserLocation | null` with no side effects. Consumers own
 * all filter/URL/state reactions to location changes and should tolerate more
 * than one update.
 */
export function useUserLocation(): UserLocation | null {
    return React.useSyncExternalStore(
        subscribeUserLocation,
        getUserLocationSnapshot,
        getUserLocationServerSnapshot
    );
}
